import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import { DemoFileUpload } from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

export function AIExplorerWorkingWithContentPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-explorer.working-with-content" })

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList
          data={ensureArray<string>(t("learning_objectives.objectives", { returnObjects: true }))}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.description"))}</p>
        <p>{t("overview.chapter_content")}</p>
        <CourseList data={ensureArray<string>(t("overview.topics", { returnObjects: true }))} />
      </CourseSection>

      <CourseSection>
        <h2>{t("process.title")}</h2>
        <CourseList data={ensureArray<string>(t("process.steps", { returnObjects: true }))} />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("process.reminder"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{t("file_handling.title")}</h2>
        <CourseList
          data={ensureArray<string>(t("file_handling.locations", { returnObjects: true }))}
        />
        <div className="my-4">
          <DemoFileUpload />
        </div>
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("file_handling.tip"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{t("documents.title")}</h2>
        <h3>{t("documents.how_it_works.title")}</h3>
        <p>{parse(t("documents.how_it_works.description"))}</p>
        <CourseList
          data={ensureArray<string>(t("documents.how_it_works.features", { returnObjects: true }))}
        />

        <h3>{t("documents.use_cases.title")}</h3>
        {ensureArray<{ title: string; points: string[]; effect_label: string; effect: string }>(
          t("documents.use_cases.cases", { returnObjects: true })
        ).map((useCase, index) => (
          <div key={index}>
            <h4>{useCase.title}</h4>
            <CourseList data={ensureArray<string>(useCase.points)} />
            <p>
              <strong>{useCase.effect_label}</strong> {parse(useCase.effect)}
            </p>
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("common_mistakes.title")}</h2>
        <CourseList
          data={ensureArray<string>(t("common_mistakes.mistakes", { returnObjects: true }))}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("images.title")}</h2>
        <h3>{t("images.how_it_works.title")}</h3>
        <CourseList
          data={ensureArray<string>(t("images.how_it_works.features", { returnObjects: true }))}
        />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("images.how_it_works.note"))}</p>
        </blockquote>

        <h3>{t("images.use_cases.title")}</h3>
        {ensureArray<{ title: string; points: string[]; effect_label: string; effect: string }>(
          t("images.use_cases.cases", { returnObjects: true })
        ).map((useCase, index) => (
          <div key={index}>
            <h4>{useCase.title}</h4>
            <CourseList data={ensureArray<string>(useCase.points)} />
            <p>
              <strong>{useCase.effect_label}</strong> {parse(useCase.effect)}
            </p>
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("links.title")}</h2>
        <h3>{t("links.how_it_works.title")}</h3>
        <p>{parse(t("links.how_it_works.description"))}</p>
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("links.how_it_works.tip"))}</p>
        </blockquote>

        <h3>{t("links.use_cases.title")}</h3>
        {ensureArray<{ title: string; points: string[]; effect_label?: string; effect?: string }>(
          t("links.use_cases.cases", { returnObjects: true })
        ).map((useCase, index) => (
          <div key={index}>
            <h4>{useCase.title}</h4>
            <CourseList data={ensureArray<string>(useCase.points)} />
            {useCase.effect && (
              <p>
                <strong>{useCase.effect_label}</strong> {parse(useCase.effect)}
              </p>
            )}
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("code_interpreter.title")}</h2>
        <p>{parse(t("code_interpreter.description"))}</p>
        <p>{t("code_interpreter.notice_when")}</p>
        <CourseList
          data={ensureArray<string>(t("code_interpreter.indicators", { returnObjects: true }))}
        />
        <p>{t("code_interpreter.focus_intro")}</p>
        <CourseList
          data={ensureArray<string>(t("code_interpreter.focus_points", { returnObjects: true }))}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={ensureArray<string>(t("summary.points", { returnObjects: true }))} />
        <p>{parse(t("summary.next_module"))}</p>
      </CourseSection>
    </CourseLayout>
  )
}
